import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ToastDismissibleBar } from './components/ToastDismissibleBar';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ButtonAlertsPendingSyncProvider } from './contexts/ButtonAlertsPendingSyncContext';
import { StreamingModeProvider } from './contexts/StreamingModeContext';
import { DownloadManagerProvider } from './contexts/DownloadManagerContext';
import { NotificationSocketProvider } from './contexts/NotificationSocketContext';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CamerasPage from './pages/CamerasPage';
import StreamsPage from './pages/StreamsPage';
import RecordingsPage from './pages/RecordingsPage';
import DownloadCertificatesPage from './pages/DownloadCertificatesPage';
import AnalyticsPage from './pages/AnalyticsPage';
import MapPage from './pages/MapPage';
import SitesPage from './pages/SitesPage';
import HistoricPage from './pages/HistoricPage';
import InventoryPage from './pages/InventoryPage';
import MiniIOTPage from './pages/MiniIOTPage';
import ButtonAlertsDashboardPage from './pages/ButtonAlertsDashboardPage';
import RolesPage from './pages/RolesPage';
import UsersPage from './pages/UsersPage';
import UserAuditPage from './pages/UserAuditPage';
import ProfilePage from './pages/ProfilePage';
import PublicAddressPage from './pages/PublicAddressPage';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import GlobalNotificationListener from './components/GlobalNotificationListener';
import { firstAllowedPath } from './utils/rbacDefaultRoute';
import { hasFullRbacMenuBypass } from './utils/rbacSecurityAccess';

const AppRoutes: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const homePath = firstAllowedPath(
    user?.allowedModules,
    hasFullRbacMenuBypass(user),
    user?.allowedActions
  );
  
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <GlobalNotificationListener />
      <Routes>
        <Route 
          path="/login" 
          element={isAuthenticated ? <Navigate to={homePath} replace /> : <LoginPage />} 
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <DashboardPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cameras"
          element={
            <ProtectedRoute>
              <Layout>
                <CamerasPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/streams"
          element={
            <ProtectedRoute>
              <Layout>
                <StreamsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/recordings"
          element={
            <ProtectedRoute>
              <Layout>
                <RecordingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/download-certificates"
          element={
            <ProtectedRoute>
              <Layout>
                <DownloadCertificatesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <Layout>
                <AnalyticsPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/map"
          element={
            <ProtectedRoute>
              <Layout>
                <MapPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/sites"
          element={
            <ProtectedRoute>
              <Layout>
                <SitesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/historic"
          element={
            <ProtectedRoute>
              <Layout>
                <HistoricPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/inventory"
          element={
            <ProtectedRoute>
              <Layout>
                <InventoryPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/miniiot"
          element={
            <ProtectedRoute>
              <Layout>
                <MiniIOTPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/button-alerts"
          element={
            <ProtectedRoute>
              <Layout>
                <ButtonAlertsDashboardPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/public-address"
          element={
            <ProtectedRoute>
              <Layout>
                <PublicAddressPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/security/roles"
          element={
            <ProtectedRoute>
              <Layout>
                <RolesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/security/users"
          element={
            <ProtectedRoute>
              <Layout>
                <UsersPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/security/audit"
          element={
            <ProtectedRoute>
              <Layout>
                <UserAuditPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Layout>
                <ProfilePage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/" 
          element={<Navigate to={isAuthenticated ? homePath : '/login'} replace />} 
        />
      </Routes>
    </BrowserRouter>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <NotificationSocketProvider>
        <ButtonAlertsPendingSyncProvider>
          <StreamingModeProvider>
            <DownloadManagerProvider>
              <AppRoutes />
        <Toaster
          position="top-right"
          reverseOrder={false}
          gutter={8}
          containerStyle={{
            /** Por encima del sidebar derecho de esquema (10000–10001), modales SPOA (~21000), etc. */
            zIndex: 50000,
          }}
          toastOptions={{
            duration: 3000,
            style: {
              borderRadius: '12px',
              padding: '16px 20px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#fff',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
              maxWidth: '400px',
            },
            success: {
              duration: 3000,
              style: {
                background: '#10B981',
                color: '#fff',
              },
              iconTheme: {
                primary: '#fff',
                secondary: '#10B981',
              },
            },
            error: {
              duration: 4000,
              style: {
                background: '#EF4444',
                color: '#fff',
              },
              iconTheme: {
                primary: '#fff',
                secondary: '#EF4444',
              },
            },
            loading: {
              style: {
                background: '#3B82F6',
                color: '#fff',
              },
              iconTheme: {
                primary: '#fff',
                secondary: '#3B82F6',
              },
            },
          }}
        >
          {(t) => <ToastDismissibleBar toast={t} defaultPosition="top-right" />}
        </Toaster>
            </DownloadManagerProvider>
          </StreamingModeProvider>
        </ButtonAlertsPendingSyncProvider>
      </NotificationSocketProvider>
    </AuthProvider>
  );
};

export default App;

